import {
  EXECUTION_TIMEOUT_SECONDS,
  executionTimeoutVariables,
} from "../execution-limits";
import "./codex-run-events";
import { applyKnowledgeStop, isExecutionStopped } from "./knowledge-run-stop";
import { LitElement, html, nothing, type PropertyValues } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import {
  api,
  type CodexModel,
  type CodexProtocol,
  type CodexConfigSource,
  type CodexRun,
  type JsonObject,
  type KnowledgeUpdateAudit,
  type KnowledgeUpdateRepository,
  type KnowledgeUpdateStop,
} from "../api/client";
import { ApiError, executionError } from "../api/errors";
import { session } from "../auth/session";
import { i18n } from "../i18n";
import "./a2k-skill-multiselect";
import "./app-dialog";
import {
  configPayload,
  configSourceField,
  configSourceLabel,
} from "./codex-config-source";

const urls: Record<CodexProtocol, string> = {
  openai: "https://api.openai.com/v1",
  anthropic: "https://api.anthropic.com/v1/messages",
};
// Only non-secret run identities live across page navigation. Keys include the
// signed-in user; a source-name alias lets both entry points resume the same run.
const activeScopes = new Map<string, string>();
const pendingScopes = new Set<string>();
interface ExistingUpdate {
  request_id: string;
  repositories: string[];
}

@customElement("knowledge-update-dialog")
export class KnowledgeUpdateDialog extends LitElement {
  @property({ type: Boolean }) open = false;
  @property({ attribute: false }) repositories: KnowledgeUpdateRepository[] =
    [];
  @state() private protocol: CodexProtocol = "openai";
  @state() private configSource: CodexConfigSource = "custom";
  @state() private effort = "medium";
  @state() private baseUrl = urls.openai;
  @state() private model = "custom";
  @state() private customModel = "";
  @state() private models: CodexModel[] = [];
  @state() private modelsBusy = false;
  @state() private catalogError = "";
  @state() private starting = false;
  @state() private requestId = "";
  @state() private run: CodexRun | null = null;
  @state() private error = "";
  @state() private conflictingRuns: ExistingUpdate[] = [];
  @state() private existingRun: ExistingUpdate | null = null;
  private scope = "";
  private modelChosen = false;
  private modelVersion = 0;
  private pollVersion = 0;
  private pollBusy = false;
  private timer?: number;

  protected createRenderRoot() {
    return this;
  }

  connectedCallback() {
    super.connectedCallback();
    window.addEventListener("languagechange", this.languageChanged);
    window.addEventListener("sessionchange", this.sessionChanged);
  }

  disconnectedCallback() {
    this.stopPolling();
    this.modelVersion++;
    window.removeEventListener("languagechange", this.languageChanged);
    window.removeEventListener("sessionchange", this.sessionChanged);
    super.disconnectedCallback();
  }

  private languageChanged = () => this.requestUpdate();
  private sessionChanged = () => {
    if (session.user?.role !== "administrator") this.close();
  };

  protected updated(changed: PropertyValues<this>) {
    if ((changed.has("open") || changed.has("repositories")) && this.open) {
      this.prepare();
    }
  }

  private scopeKey(ids = this.repositories.map((repo) => repo.id)) {
    return JSON.stringify([session.user?.id, [...new Set(ids)].sort()]);
  }

  private prepare() {
    const scope = this.scopeKey();
    if (scope !== this.scope) {
      this.stopPolling();
      this.scope = scope;
      this.requestId = activeScopes.get(scope) || "";
      this.run = null;
      this.error = "";
      this.conflictingRuns = [];
      this.existingRun = null;
    }
    if (this.requestId) void this.poll();
    else void this.loadModels();
  }

  private stopPolling() {
    window.clearTimeout(this.timer);
    this.pollVersion++;
    this.pollBusy = false;
  }

  private close() {
    if (this.starting) return;
    this.open = false;
    this.stopPolling();
    this.modelVersion++;
    this.dispatchEvent(new CustomEvent("update-close"));
  }

  private changeProtocol(value: CodexProtocol) {
    const current = this.baseUrl.trim().replace(/\/+$/, "");
    if (!current || Object.values(urls).includes(current))
      this.baseUrl = urls[value];
    this.protocol = value;
    if (value === "anthropic") {
      this.modelChosen = true;
      this.model = "custom";
    }
  }

  private changeConfigSource(value: CodexConfigSource) {
    this.configSource = value;
    this.modelChosen = value === "local";
    this.model = value === "local" ? "" : this.models[0]?.value || "custom";
    this.effort = value === "local" ? "" : "medium";
  }

  private async loadModels() {
    const version = ++this.modelVersion;
    this.modelsBusy = true;
    this.catalogError = "";
    try {
      const result = await api.codexModels();
      if (version !== this.modelVersion || !this.open) return;
      this.models = result.models;
      if (
        this.configSource === "custom" &&
        this.protocol === "openai" &&
        !this.modelChosen
      ) {
        this.model = this.models[0]?.value || "custom";
      }
    } catch (error) {
      if (version === this.modelVersion)
        this.catalogError = (error as Error).message;
    } finally {
      if (version === this.modelVersion) this.modelsBusy = false;
    }
  }

  private async submit(event: SubmitEvent) {
    event.preventDefault();
    const form = event.currentTarget as HTMLFormElement;
    if (!form.reportValidity()) return;
    await this.start(Object.fromEntries(new FormData(form)));
    const key = form.querySelector<HTMLInputElement>("input[name=apikey]");
    if (key) key.value = "";
  }

  private async start(values: JsonObject) {
    if (session.user?.role !== "administrator") {
      this.error = i18n.t("knowledgeUpdateAdminOnly");
      return;
    }
    if (!this.repositories.length) {
      this.error = i18n.t("knowledgeUpdateEmpty");
      return;
    }
    if (this.repositories.some((repo) => repo.ready === false)) {
      this.error = i18n.t("knowledgeUpdateNotReady");
      return;
    }
    const scope = this.scopeKey();
    if (this.starting || this.requestId || pendingScopes.has(scope)) return;
    const existing = activeScopes.get(scope);
    if (existing) {
      this.requestId = existing;
      void this.poll();
      return;
    }
    this.starting = true;
    pendingScopes.add(scope);
    this.error = "";
    this.conflictingRuns = [];
    const key = String(values.apikey || "");
    const secrets = [key];
    try {
      const result = await api.startKnowledgeUpdate({
        ...configPayload(this.configSource, {
          protocol: this.protocol,
          apikey: key,
          base_url: this.baseUrl,
          model: this.model,
          ...(this.model === "custom"
            ? { custom_model: this.customModel }
            : {}),
          reasoning_effort: String(values.reasoning_effort ?? this.effort),
        }),
        repositories: this.repositories.map((repo) => repo.id),
        timeout_seconds: Number(
          values.timeout_seconds || EXECUTION_TIMEOUT_SECONDS,
        ),
      });
      this.requestId = result.request_id;
      activeScopes.set(scope, result.request_id);
      activeScopes.set(this.scopeKey(result.repositories), result.request_id);
      this.run = null;
      this.dispatchEvent(
        new CustomEvent("update-started", {
          detail: { request_id: result.request_id },
        }),
      );
      void this.poll();
    } catch (error) {
      if (
        error instanceof ApiError &&
        error.code === "knowledge_update_running"
      ) {
        const details = error.details as JsonObject | undefined;
        this.conflictingRuns = Array.isArray(details?.runs)
          ? details.runs.filter((run): run is ExistingUpdate =>
              Boolean(
                run &&
                typeof run === "object" &&
                typeof run.request_id === "string" &&
                Array.isArray(run.repositories) &&
                run.repositories.every(
                  (name: unknown) => typeof name === "string",
                ),
              ),
            )
          : [];
        this.error = i18n.t("knowledgeUpdateAlreadyRunning");
        return;
      }
      this.error = secrets
        .filter(Boolean)
        .reduce(
          (message, secret) => message.split(secret).join("[redacted]"),
          (error as Error).message,
        );
    } finally {
      pendingScopes.delete(scope);
      this.starting = false;
    }
  }

  private showExisting(run: ExistingUpdate) {
    this.stopPolling();
    this.existingRun = run;
    this.requestId = run.request_id;
    this.run = null;
    this.error = "";
    void this.poll();
  }

  private async poll() {
    if (!this.requestId || this.pollBusy || !this.open) return;
    const id = this.requestId;
    const version = ++this.pollVersion;
    this.pollBusy = true;
    window.clearTimeout(this.timer);
    try {
      const result = await api.codexRun(id);
      if (version !== this.pollVersion || id !== this.requestId) return;
      this.run = result.run;
      this.error = "";
      if (!["queued", "running"].includes(this.run.status)) {
        for (const [scope, requestId] of activeScopes) {
          if (requestId === id) activeScopes.delete(scope);
        }
        this.dispatchEvent(
          new CustomEvent("update-completed", {
            detail: { request_id: id, status: this.run.status },
          }),
        );
      }
    } catch (error) {
      if (version === this.pollVersion) this.error = (error as Error).message;
    } finally {
      if (version === this.pollVersion) {
        this.pollBusy = false;
        if (
          this.open &&
          this.isConnected &&
          (!this.run || ["queued", "running"].includes(this.run.status))
        ) {
          this.timer = window.setTimeout(() => void this.poll(), 3000);
        }
      }
    }
  }

  private startAnother() {
    if (
      this.starting ||
      !this.run ||
      ["queued", "running"].includes(this.run.status)
    )
      return;
    this.stopPolling();
    this.requestId = "";
    this.run = null;
    this.error = "";
    this.conflictingRuns = [];
    this.existingRun = null;
    void this.loadModels();
  }

  private progress() {
    const run = this.run;
    const audit = run?.audit?.knowledge_update as
      KnowledgeUpdateAudit | undefined;
    const status = run?.status || "queued";
    const label =
      run && isExecutionStopped(run)
        ? "knowledgeUpdateStopped"
        : run?.stopping ||
            (run?.stop_requested && ["queued", "running"].includes(status))
          ? "knowledgeUpdateStopping"
          : status === "queued"
            ? "knowledgeUpdateQueued"
            : status === "running"
              ? "codexHistoryRunning"
              : status === "success"
                ? "codexHistorySucceeded"
                : "codexHistoryFailed";
    return html`<p role="status">
        <strong>${i18n.t(label)}</strong> · <code>${this.requestId}</code>
      </p>
      <p>
        ${i18n.t("codexConfigSource")}:
        ${configSourceLabel(run?.config_source || this.configSource)}
      </p>
      ${this.error ? html`<p class="status status-error" role="alert">${this.error}</p>` : nothing}
      <knowledge-run-stop
        .run=${run}
        @stop-state=${(event: CustomEvent<KnowledgeUpdateStop>) => {
          if (this.run?.id === event.detail.request_id)
            this.run = applyKnowledgeStop(this.run, event.detail);
          void this.poll();
        }}
      ></knowledge-run-stop>
      <codex-run-events
        .runId=${this.requestId}
        .active=${["queued", "running"].includes(status)}
      ></codex-run-events>
      ${run?.error && !isExecutionStopped(run) ? html`<p class="status status-error" role="alert">${executionError(run.error)}</p>` : nothing}
      <h3>${i18n.t("knowledgeUpdateVersions")}</h3>
      ${(audit?.source_versions || []).map((source) => html`<p>${source.source_name}: <code>${source.head}</code></p>`)}
      <h3>${i18n.t("knowledgeUpdateValidation")}</h3>
      ${audit?.coverage_state ? html`<p role="status">${i18n.t(audit.coverage_state === "complete" ? "knowledgeUpdateCoverageComplete" : "knowledgeUpdateCoveragePartial")}</p>` : nothing}
      ${audit?.validation_summary ? html`<pre class="code-output codex-history-output">${JSON.stringify(audit.validation_summary, null, 2)}</pre>` : html`<p>${i18n.t("knowledgeUpdateValidationPending")}</p>`}
      <h3>${i18n.t("codexHistoryOutput")}</h3>
      <pre class="code-output codex-history-output">
${run?.output || run?.text || "—"}</pre>
      <p><a href="/console/codex">${i18n.t("knowledgeUpdateHistory")}</a></p>
      <a2k-button type="button" @click=${() => this.poll()}>
        ${i18n.t("refresh")}
      </a2k-button>
      ${run && !["queued", "running"].includes(status) ? html`<a2k-button type="button" @click=${() => this.startAnother()}>${i18n.t("knowledgeUpdateAgain")}</a2k-button>` : nothing}`;
  }

  private configuration() {
    return html`<form class="codex-form" @submit=${this.submit}>
      ${configSourceField(this.configSource, (value) => this.changeConfigSource(value))}
      ${
        this.configSource === "custom"
          ? html`
              <label class="native-field"
                ><span>${i18n.t("codexProviderKey")}</span
                ><input
                  class="a2k-native-input"
                  name="apikey"
                  type="password"
                  autocomplete="off"
                  required
              /></label>
              <label class="native-field"
                ><span>${i18n.t("codexProtocol")}</span
                ><select
                  class="a2k-native-input"
                  name="protocol"
                  .value=${this.protocol}
                  @change=${(event: Event) => this.changeProtocol((event.target as HTMLSelectElement).value as CodexProtocol)}
                >
                  <option value="openai">OpenAI</option>
                  <option value="anthropic">Anthropic</option>
                </select></label
              >
              <label class="native-field"
                ><span>${i18n.t("codexBaseUrl")}</span
                ><input
                  class="a2k-native-input"
                  name="base_url"
                  .value=${this.baseUrl}
                  @input=${(event: Event) => {
                    this.baseUrl = (event.target as HTMLInputElement).value;
                  }}
                  required
              /></label>
            `
          : nothing
      }
      <label class="native-field"
        ><span>${i18n.t("codexModel")}</span
        ><select
          class="a2k-native-input"
          name="model"
          .value=${this.model}
          @change=${(event: Event) => {
            this.modelChosen = true;
            this.model = (event.target as HTMLSelectElement).value;
          }}
        >
          ${this.configSource === "local" ? html`<option value="">${i18n.t("codexConfigInherit")}</option>` : nothing}
          ${(this.configSource === "local" || this.protocol === "openai" ? this.models : []).map((model) => html`<option value=${model.value}>${model.label || model.value} (${model.value})</option>`)}
          <option value="custom">${i18n.t("codexCustomModel")}</option>
        </select></label
      >
      ${
        this.model === "custom"
          ? html`<label class="native-field"
              ><span>${i18n.t("codexCustomModel")}</span
              ><input
                class="a2k-native-input"
                name="custom_model"
                .value=${this.customModel}
                @input=${(event: Event) => {
                  this.modelChosen = true;
                  this.customModel = (event.target as HTMLInputElement).value;
                }}
                required
            /></label>`
          : nothing
      }
      ${this.protocol === "openai" ? html`<div class="wide" role="status">${this.modelsBusy ? i18n.t("loading") : nothing}${this.catalogError || (!this.modelsBusy && !this.models.length) ? html`<p>${i18n.t("knowledgeUpdateCatalogError")} ${this.catalogError}</p>` : nothing}<a2k-button type="button" ?disabled=${this.modelsBusy} @click=${() => this.loadModels()}>${i18n.t("refresh")}</a2k-button></div>` : nothing}
      <label class="native-field"
        ><span>${i18n.t("codexEffort")}</span
        ><select
          class="a2k-native-input"
          name="reasoning_effort"
          .value=${this.effort}
          @change=${(event: Event) => {
            this.effort = (event.target as HTMLSelectElement).value;
          }}
        >
          ${this.configSource === "local" ? html`<option value="">${i18n.t("codexConfigInherit")}</option>` : nothing}
          <option value="low">low</option>
          <option value="medium">medium</option>
          <option value="high">high</option>
          <option value="xhigh">xhigh</option>
          <option value="max">max</option>
          <option value="ultra">ultra</option>
        </select></label
      >
      <label class="native-field"
        ><span>${i18n.t("codexTimeoutSeconds", executionTimeoutVariables)}</span
        ><input
          class="a2k-native-input"
          name="timeout_seconds"
          type="number"
          min="1"
          step="1"
          max=${EXECUTION_TIMEOUT_SECONDS}
          value=${EXECUTION_TIMEOUT_SECONDS}
          required
      /></label>
      <div class="wide skill-picker">
        <a2k-skill-multiselect
          .skills=${[{ name: "compat-knowledge-update" }]}
          .selected=${["compat-knowledge-update"]}
          .disabled=${true}
        ></a2k-skill-multiselect>
      </div>
      ${this.error ? html`<p class="wide status status-error" role="alert">${this.error}</p>` : nothing}
      ${this.conflictingRuns.map(
        (run) =>
          html`<div class="wide">
            <code>${run.request_id}</code> · ${run.repositories.join(", ")}
            <a2k-button type="button" @click=${() => this.showExisting(run)}
              >${i18n.t("knowledgeUpdateViewExisting")}</a2k-button
            >
          </div>`,
      )}
      <a2k-button
        type="submit"
        ?disabled=${this.starting || !this.repositories.length || this.repositories.some((repo) => repo.ready === false) || session.user?.role !== "administrator"}
      >
        ${i18n.t(this.starting ? "loading" : "knowledgeUpdateStart")}
      </a2k-button>
    </form>`;
  }

  render() {
    if (!this.open) return nothing;
    return html`<app-dialog
      .open=${true}
      heading=${i18n.t("knowledgeUpdate")}
      cancel-label=${i18n.t("close")}
      .hideConfirm=${true}
      .busy=${this.starting}
      @dialog-cancel=${() => this.close()}
    >
      <h3>${i18n.t("knowledgeUpdateScope")}</h3>
      <p>${i18n.t("knowledgeUpdateScopeHelp")}</p>
      <ul>
        ${this.existingRun ? this.existingRun.repositories.map((name) => html`<li><code>${name}</code></li>`) : this.repositories.map((repo) => html`<li>${repo.label} <code>${repo.id}</code></li>`)}
      </ul>
      ${session.user?.role !== "administrator" ? html`<p class="status status-error">${i18n.t("knowledgeUpdateAdminOnly")}</p>` : this.repositories.some((repo) => repo.ready === false) ? html`<p class="status status-error">${i18n.t("knowledgeUpdateNotReady")}</p>` : nothing}
      ${this.requestId ? this.progress() : this.configuration()}
    </app-dialog>`;
  }
}
